import classNames from 'classnames';
import { inflect } from 'inflection';
import PropTypes from 'prop-types';
import React from 'react';
import { useDragDropManager } from 'react-dnd';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import archiveIcon from '../../icons/non-prefilled/16x16/archive.svg';
import historyIcon from '../../icons/non-prefilled/16x16/history.svg';
import pinOutlineIcon from '../../icons/non-prefilled/16x16/pinOutline.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import unpinOutlineIcon from '../../icons/non-prefilled/16x16/unpinOutline.svg';
import PostDetailPreviewPopOver from '../../post/PostDetailPreviewPopOver';
import PostTitleWithIcons from '../../post/PostTitleWithIcons';
import postPath from '../../utils/url/postPath';
import {
  DragTypes,
  isTopicPostArchived,
  isTopicPostDeleted,
  isTopicPostPublished,
  isTopicPostTemplate,
  SelectedPostsShape,
  TopicPostAction,
  topicPostActionCheck,
  TopicReorganizationMetadataShape,
  TopicReorganizationTopicPostShape,
  TopicReorganizationTreeShape,
  TopicSelectedShape,
} from '../useTopicReorganization';
import TopicTreeCardDropSeparator from './TopicTreeCardDropSeparator';
import TopicTreeCardItem from './TopicTreeCardItem';
import TopicTreeCardItemDraggable from './TopicTreeCardItemDraggable';
import styles from './TopicTreeCardPost.module.scss';
import TopicTreeCardPostIcon from './TopicTreeCardPostIcon';
import isTopicPostPinned from '../utils/isTopicPostPinned';
import { MoveStates } from '../constants';

const ActionMenuItem = ({
  title,
  topicId,
  selectedPosts,
  action,
  topicPost,
  separator,
  onItemClick,
  ...props
}) => {
  let targets = [];
  if (topicPost.isSelected) {
    targets = selectedPosts.byAllowedActions[action];
  } else if (topicPostActionCheck(topicPost, topicId, action)) {
    targets = [topicPost.postKey];
  }
  if (!targets.length) return null;

  const menuItemTitle = typeof title === 'function' ? title(targets) : title;
  return (
    <>
      {separator && <MenuSeparator />}
      <MenuItem
        {...props}
        onClick={() => {
          onItemClick(targets);
        }}
      >
        {topicPost.isSelected && selectedPosts.all.length > 0
          ? `${menuItemTitle} (${targets.length})`
          : menuItemTitle}
      </MenuItem>
    </>
  );
};

ActionMenuItem.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  topicId: PropTypes.string.isRequired,
  selectedPosts: SelectedPostsShape.isRequired,
  action: PropTypes.oneOf(Object.values(TopicPostAction)).isRequired,
  topicPost: TopicReorganizationTopicPostShape.isRequired,
  separator: PropTypes.bool,
  onItemClick: PropTypes.func.isRequired,
};

ActionMenuItem.defaultProps = {
  separator: false,
};

const propTypes = {
  topicPost: TopicReorganizationTopicPostShape.isRequired,
  reorganizable: PropTypes.bool.isRequired,
  ancestorsReorganizationMetadata: PropTypes.arrayOf(
    TopicReorganizationMetadataShape,
  ).isRequired,
  index: PropTypes.number.isRequired,
  lastPinnedPostIndex: PropTypes.number.isRequired,
  isReorganizing: PropTypes.bool.isRequired,
  columnSelected: TopicSelectedShape.isRequired,
  selectedPosts: SelectedPostsShape.isRequired,
  onPostSelectedChange: PropTypes.func.isRequired,
  onPostDeletedChange: PropTypes.func.isRequired,
  onPostPinnedChange: PropTypes.func.isRequired,
  onPostPublishedChange: PropTypes.func.isRequired,
  onPostArchivedChange: PropTypes.func.isRequired,
  getDropParams: PropTypes.func.isRequired,
  tree: TopicReorganizationTreeShape.isRequired,
};

const TopicTreeCardPost = ({
  topicPost,
  reorganizable,
  ancestorsReorganizationMetadata,
  index,
  lastPinnedPostIndex,
  columnSelected,
  selectedPosts,
  getDropParams,
  isReorganizing,
  onPostSelectedChange,
  onPostDeletedChange,
  onPostPinnedChange,
  onPostArchivedChange,
  tree,
}) => {
  const dragDropManager = useDragDropManager();
  const isDragging = dragDropManager.getMonitor().isDragging() ?? false;

  const getSelected = (p) =>
    isTopicPostPinned(p)
      ? { postKeys: [], pinnedPostKeys: [p.postKey], topicIds: [] }
      : { postKeys: [p.postKey], pinnedPostKeys: [], topicIds: [] };

  const { reorganizationMetadata } = topicPost;
  const isMovedOut = reorganizationMetadata.move.state === MoveStates.movedOut;

  const isDeleted = isTopicPostDeleted(topicPost);
  const isPinned = isTopicPostPinned(topicPost);
  const isPublished = isTopicPostPublished(topicPost);
  const isArchived = isTopicPostArchived(topicPost);
  const isTemplate = isTopicPostTemplate(topicPost);

  return (
    <PostDetailPreviewPopOver
      postId={topicPost.node.id}
      controls={{ preventOpening: isDragging }}
    >
      <TopicTreeCardItem
        type="post"
        reorganizable={reorganizable}
        reorganizationMetadata={reorganizationMetadata}
        ancestorsReorganizationMetadata={ancestorsReorganizationMetadata}
        titleLine={
          <>
            {isReorganizing && index <= lastPinnedPostIndex && (
              <TopicTreeCardDropSeparator
                {...getDropParams([DragTypes.pinnedPosts], {
                  destinationTree: tree,
                  pinnedPostIndexWithPlaceholder: index,
                })}
              />
            )}
            <TopicTreeCardItemDraggable
              type="post"
              to={postPath(topicPost.node)}
              onCheckboxChange={() =>
                onPostSelectedChange({
                  postKey: topicPost.postKey,
                  selected: !topicPost.isSelected,
                })
              }
              reorganizable={reorganizable}
              isChecked={topicPost.isSelected}
              isCheckboxDisabled={topicPost.isParentSelected}
              isReorganizing={isReorganizing}
              isMovedOut={isMovedOut}
              toMove={
                topicPost.isSelected ? columnSelected : getSelected(topicPost)
              }
            >
              <div
                className={classNames(styles.content, {
                  [styles.deleted]: isDeleted,
                  [styles.archived]: isArchived,
                })}
              >
                <TopicTreeCardPostIcon
                  pinned={isPinned}
                  published={isPublished}
                  archived={isArchived}
                  template={isTemplate}
                  movedOut={isMovedOut}
                  moveState={reorganizationMetadata.move.state}
                />
                <div className={styles.title}>
                  <PostTitleWithIcons
                    post={topicPost.node}
                    iconSize="small"
                    truncate="single"
                  />
                </div>
              </div>
            </TopicTreeCardItemDraggable>
            {isReorganizing && index === lastPinnedPostIndex && (
              <TopicTreeCardDropSeparator
                {...getDropParams([DragTypes.pinnedPosts], {
                  destinationTree: tree,
                  pinnedPostIndexWithPlaceholder: lastPinnedPostIndex + 1,
                })}
              />
            )}
          </>
        }
        menu={
          isReorganizing &&
          reorganizable &&
          !isMovedOut && (
            <>
              <ActionMenuItem
                title="Restore"
                icon={historyIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={TopicPostAction.restore}
                topicPost={topicPost}
                onItemClick={(targets) => {
                  onPostDeletedChange({
                    postKeys: targets,
                    deleted: false,
                  });
                }}
              />
              <ActionMenuItem
                title="Pin"
                icon={pinOutlineIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={TopicPostAction.pin}
                topicPost={topicPost}
                onItemClick={(targets) => {
                  onPostPinnedChange({
                    postKeys: targets,
                    pinned: true,
                  });
                }}
              />
              <ActionMenuItem
                title="Unpin"
                icon={unpinOutlineIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={TopicPostAction.unpin}
                topicPost={topicPost}
                onItemClick={(targets) =>
                  onPostPinnedChange({
                    postKeys: targets,
                    pinned: false,
                  })
                }
              />
              <ActionMenuItem
                title="Archive"
                icon={archiveIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={TopicPostAction.archive}
                topicPost={topicPost}
                onItemClick={(targets) => {
                  onPostArchivedChange({
                    postKeys: targets,
                    archived: true,
                  });
                }}
              />
              <ActionMenuItem
                title="Unarchive"
                icon={unarchiveIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={TopicPostAction.unarchive}
                topicPost={topicPost}
                onItemClick={(targets) => {
                  onPostArchivedChange({
                    postKeys: targets,
                    archived: false,
                  });
                }}
              />
              <ActionMenuItem
                danger
                separator
                title={(targets) => `Delete ${inflect('post', targets.length)}`}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={TopicPostAction.delete}
                topicPost={topicPost}
                onItemClick={(targets) => {
                  onPostDeletedChange({
                    postKeys: targets,
                    deleted: true,
                  });
                }}
              />
            </>
          )
        }
      />
    </PostDetailPreviewPopOver>
  );
};

TopicTreeCardPost.propTypes = propTypes;

export default TopicTreeCardPost;
